import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, Button, LinearProgress, Checkbox, IconButton, useTheme, useMediaQuery, Avatar, TextField, CircularProgress } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Card, Fade } from '@mui/material';
import api from '../../services/api';
import { Course, Lesson } from '../../types/course';
import BlockRenderer from '../../components/course/BlockRenderer';
import CourseLoadingAnimation from '../../components/learner/CourseLoadingAnimation';
import { decodeHTMLEntities } from '../../utils/stringUtils';

import LessonQA from '../../components/learner/LessonQA';
import ReportProblemModal from '../../components/common/ReportProblemModal';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import QuizPlayer from '../../components/learner/QuizPlayer';

const drawerWidth = 300;

const CoursePlayer = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const navigate = useNavigate();
    const [course, setCourse] = useState<Course | null>(null);
    const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
    const [progress, setProgress] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showQA, setShowQA] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [quizScore, setQuizScore] = useState<number | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [showCorrection, setShowCorrection] = useState(false);
    const [learnerAnswer, setLearnerAnswer] = useState('');
    const [aiFeedback, setAiFeedback] = useState<any>(null);
    const [aiCorrectionCount, setAiCorrectionCount] = useState(0);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const startTimeRef = useRef<number>(Date.now());
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Scroll to top and reset exercise state when lesson changes
    useEffect(() => {
        if (currentLesson && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
        setLearnerAnswer('');
        setAiFeedback(null);
        setAiCorrectionCount(0);
        setIsEvaluating(false);
        setShowCorrection(false);
    }, [currentLesson?._id]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Track time spent on current lesson
    useEffect(() => {
        if (!currentLesson) return;

        // Reset start time when lesson changes
        startTimeRef.current = Date.now();

        return () => {
            // Calculate duration when leaving the lesson (unmount or switch)
            const duration = (Date.now() - startTimeRef.current) / 1000;
            if (duration > 1) {
                // Send time update (without changing status)
                updateProgress(currentLesson._id, undefined, Math.round(duration));
            }
        };
    }, [currentLesson?._id]);

    useEffect(() => {
        fetchCourseAndProgress();
    }, [courseId]);

    const fetchCourseAndProgress = async () => {
        try {
            const [courseRes, progressRes] = await Promise.all([
                api.get(`/api/course/course/${courseId}`),
                api.get(`/api/progress/course/${courseId}`)
            ]);
            setCourse(courseRes.data);
            setProgress(progressRes.data);

            // Check for lessonId in query params
            const searchParams = new URLSearchParams(location.search);
            const lessonIdParam = searchParams.get('lessonId');

            let initialLesson = null;

            if (lessonIdParam) {
                // Find the lesson with this ID
                for (const section of courseRes.data.sections || []) {
                    const found = section.lessons?.find((l: Lesson) => l._id === lessonIdParam);
                    if (found) {
                        initialLesson = found;
                        break;
                    }
                }
            }

            // Fallback to first lesson if not found or not provided
            if (!initialLesson && courseRes.data.sections?.[0]?.lessons?.[0]) {
                initialLesson = courseRes.data.sections[0].lessons[0];
            }

            if (initialLesson) {
                setCurrentLesson(initialLesson);
                // Set initial quiz score if it's a quiz
                if (initialLesson.type === 'quiz') {
                    const existingProgress = progressRes.data.find((p: any) => p.lessonId === initialLesson._id);
                    setQuizScore(existingProgress?.score ?? null);
                }
            }

            setLoading(false);
        } catch (err) {
            console.error("Error fetching data", err);
            setLoading(false);
        }
    };

    const handleLessonSelect = (lesson: Lesson) => {
        setCurrentLesson(lesson);
        // Sync exercise data and quiz score from current progress
        const existingProgress = progress.find(p => p.lessonId === lesson._id);
        setQuizScore(existingProgress?.score ?? null);
        
        if (lesson.type === 'exercise') {
            setLearnerAnswer(existingProgress?.data?.learnerAnswer || '');
            setAiFeedback(existingProgress?.data?.aiFeedback || null);
            setAiCorrectionCount(existingProgress?.data?.aiCorrectionCount || 0);
        }

        // Mark as started if not already
        updateProgress(lesson._id, 'started');
    };

    const updateProgress = async (lessonId: string, status?: 'started' | 'completed', timeSpent: number = 0, data?: any) => {
        try {
            await api.post('/api/progress/update', {
                courseId,
                lessonId,
                status,
                timeSpent,
                data
            });
            // Refresh progress
            const res = await api.get(`/api/progress/course/${courseId}`);
            setProgress(res.data);
        } catch (err) {
            console.error("Error updating progress", err);
        }
    };

    const isCompleted = (lessonId: string) => {
        return progress.some(p => p.lessonId === lessonId && p.status === 'completed');
    };

    const getIconForType = (type: string) => {
        switch (type) {
            case 'video': return <PlayCircleOutlineIcon />;
            case 'quiz': return <QuizIcon />;
            default: return <ArticleIcon />;
        }
    };

    const handleEvaluateExercise = async () => {
        if (!learnerAnswer.trim() || !currentLesson) return;

        setIsEvaluating(true);
        try {
            const response = await api.post('/api/course/evaluate-exercise', {
                lessonId: currentLesson._id,
                learnerAnswer
            });
            setAiFeedback(response.data);
            setAiCorrectionCount(response.data.aiCorrectionCount || (prev => prev + 1));
            // No need for updateProgress here as the backend evaluate-exercise already saves it
            // and we want to avoid overwriting aiCorrectionCount which updateProgress doesn't handle.
        } catch (err) {
            console.error("Error evaluating exercise", err);
        } finally {
            setIsEvaluating(false);
        }
    };

    const handleNext = async () => {
        if (!course || !currentLesson) return;

        // Mark current as completed with time spent
        const duration = (Date.now() - startTimeRef.current) / 1000;
        await updateProgress(currentLesson._id, 'completed', Math.round(duration), { learnerAnswer, aiFeedback });

        // Reset start time to prevent double counting in useEffect cleanup
        startTimeRef.current = Date.now();

        // Find next lesson
        let foundCurrent = false;
        let nextLesson = null;

        for (const section of course.sections || []) {
            for (const lesson of section.lessons || []) {
                if (foundCurrent) {
                    nextLesson = lesson;
                    break;
                }
                if (lesson._id === currentLesson._id) {
                    foundCurrent = true;
                }
            }
            if (nextLesson) break;
        }

        if (nextLesson) {
            handleLessonSelect(nextLesson);
        } else {
            alert("Cours terminé !");
            navigate('/learner/dashboard');
        }
    };

    const searchParams = new URLSearchParams(location.search);
    const isPreview = searchParams.get('mode') === 'preview';

    if (loading) return <CourseLoadingAnimation />;
    if (!course) return <div>Cours introuvable</div>;

    // Drawer Content
    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.05)', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/learner/dashboard')}
                    sx={{ mb: 2, textTransform: 'none', color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' } }}
                >
                    Retour au tableau de bord
                </Button>
                <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', lineHeight: 1.3, color: '#2d3748' }}>
                    {decodeHTMLEntities(course.title)}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={(progress.filter(p => p.status === 'completed').length / (course.sections?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 1)) * 100}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(0,0,0,0.05)',
                                '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }
                            }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                        {Math.round((progress.filter(p => p.status === 'completed').length / (course.sections?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 1)) * 100)}%
                    </Typography>
                </Box>
            </Box>
            <List sx={{ overflow: 'auto', flexGrow: 1, p: 2 }}>
                {course.sections?.map((section) => (
                    <React.Fragment key={section._id}>
                        <ListItem sx={{ py: 1, mt: 1 }}>
                            <ListItemText
                                primary={decodeHTMLEntities(section.title)}
                                primaryTypographyProps={{
                                    variant: 'subtitle2',
                                    fontWeight: 700,
                                    fontFamily: 'Quicksand, sans-serif',
                                    color: '#718096',
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.05em'
                                }}
                            />
                        </ListItem>
                        {section.lessons?.map((lesson) => {
                            const active = currentLesson?._id === lesson._id;
                            const completed = isCompleted(lesson._id);
                            return (
                                <ListItemButton
                                    key={lesson._id}
                                    selected={active}
                                    onClick={() => {
                                        handleLessonSelect(lesson);
                                        if (isMobile) setMobileOpen(false);
                                    }}
                                    sx={{
                                        pl: 2,
                                        borderRadius: 2,
                                        mb: 0.5,
                                        transition: 'all 0.2s',
                                        bgcolor: active ? 'rgba(102, 126, 234, 0.1) !important' : 'transparent',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                                        borderLeft: active ? '4px solid #667eea' : '4px solid transparent'
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36, color: active ? '#667eea' : (completed ? '#48bb78' : '#cbd5e0') }}>
                                        {completed ? <CheckCircleIcon fontSize="small" /> : getIconForType(lesson.type)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={decodeHTMLEntities(lesson.title)}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: active ? 600 : 500,
                                            color: active ? '#2d3748' : '#4a5568'
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </React.Fragment>
                ))}
            </List>

            {/* Action Buttons at Bottom */}
            <Box sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.05)', bgcolor: '#f8f9fa' }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ReportProblemIcon />}
                    onClick={() => setShowReportModal(true)}
                    sx={{ mb: 1, fontFamily: 'Quicksand, sans-serif', textTransform: 'none', borderRadius: 2, borderColor: '#e2e8f0', color: '#718096' }}
                >
                    Signaler un problème
                </Button>
                <Button
                    fullWidth
                    variant={showQA ? 'contained' : 'outlined'}
                    startIcon={<QuestionAnswerIcon />}
                    onClick={() => setShowQA(!showQA)}
                    sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        textTransform: 'none',
                        borderRadius: 2,
                        ...(showQA ? { bgcolor: '#667eea', '&:hover': { bgcolor: '#5a67d8' } } : { borderColor: '#e2e8f0', color: '#718096' })
                    }}
                >
                    Questions / Réponses
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f7fa' }}>
            {/* Sidebar - Hidden in preview mode */}
            {!isPreview && (
                <Box
                    component="nav"
                    sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
                >
                    {/* Mobile Drawer */}
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }} // Better open performance on mobile.
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', boxShadow: '10px 0 30px rgba(0,0,0,0.05)' },
                        }}
                    >
                        {drawerContent}
                    </Drawer>

                    {/* Desktop Drawer */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid rgba(0,0,0,0.05)' },
                        }}
                        open
                    >
                        {drawerContent}
                    </Drawer>
                </Box>
            )}

            {/* Content Area */}
            <Box component="main" sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                overflow: 'hidden',
                width: { md: `calc(100% - ${drawerWidth}px)` },
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)'
            }}>
                {/* Mobile Menu Button */}
                {!isPreview && (
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, p: 2, alignItems: 'center', bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ color: '#667eea' }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="subtitle1" component="span" sx={{ ml: 1, fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2d3748' }}>
                            {course.title}
                        </Typography>
                    </Box>
                )}

                <Box ref={scrollContainerRef} sx={{ flexGrow: 1, overflow: 'auto', width: '100%', px: { xs: 2, md: 6 }, py: { xs: 3, md: 4 } }}>
                    {currentLesson ? (
                        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
                            <Box sx={{
                                bgcolor: 'white',
                                borderRadius: 4,
                                p: { xs: 3, md: 6 },
                                boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
                                mb: 4
                            }}>
                                <Typography variant="h4" gutterBottom sx={{
                                    fontWeight: 800,
                                    fontFamily: 'Quicksand, sans-serif',
                                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                                    color: '#1a202c',
                                    mb: 4,
                                    letterSpacing: '-0.02em'
                                }}>
                                    {decodeHTMLEntities(currentLesson.title)}
                                </Typography>

                                <Divider sx={{ mb: 4, borderColor: 'rgba(0,0,0,0.05)' }} />

                                {/* Render Content based on type */}
                                {currentLesson.type === 'text' && (
                                    <Box sx={{ '& .prose': { color: '#4a5568', fontSize: '1.125rem', lineHeight: 1.8 } }}>
                                        {currentLesson.blocks?.map((block: any) => (
                                            <BlockRenderer key={block.id} block={block} />
                                        ))}
                                        {(!currentLesson.blocks || currentLesson.blocks.length === 0) && <Typography color="text.secondary">Aucun contenu.</Typography>}
                                    </Box>
                                )}

                                {currentLesson.type === 'video' && currentLesson.video && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                        <Box sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 40px -5px rgba(0,0,0,0.1)' }}>
                                            {currentLesson.video.sourceType === 'url' && currentLesson.video.player === 'youtube' ? (
                                                <iframe
                                                    width="100%"
                                                    height="500"
                                                    src={currentLesson.video.url.replace('watch?v=', 'embed/')}
                                                    title={currentLesson.video.alt || "Video player"}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    style={{ display: 'block' }}
                                                ></iframe>
                                            ) : (
                                                <video
                                                    width="100%"
                                                    controls
                                                    src={currentLesson.video.url}
                                                    style={{ display: 'block', backgroundColor: '#000' }}
                                                >
                                                    {currentLesson.video.alt}
                                                    Your browser does not support the video tag.
                                                </video>
                                            )}
                                            {currentLesson.video.description && (
                                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #edf2f7' }}>
                                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                                        {currentLesson.video.description}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {currentLesson.type === 'quiz' && currentLesson.quiz && (
                                    <Box sx={{ my: 2 }}>
                                        <QuizPlayer
                                            questions={currentLesson.quiz.questions}
                                            onComplete={async (answers) => {
                                                try {
                                                    // Send to backend for grading
                                                    const response = await api.post('/api/progress/quiz-result', {
                                                        lessonId: currentLesson._id,
                                                        courseId: courseId,
                                                        answers
                                                    });

                                                    const { score, corrections, passed } = response.data;
                                                    setQuizScore(score);

                                                    // Return corrections to QuizPlayer for display
                                                    return { score, corrections, passed };
                                                } catch (err) {
                                                    console.error('Error saving quiz result', err);
                                                    return null;
                                                }
                                            }}
                                        />
                                    </Box>
                                )}

                                {currentLesson.type === 'exercise' && currentLesson.exercise && (
                                    <Box>
                                        <Card sx={{ borderRadius: 6, mb: 4, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 10px 30px rgba(0,0,0,0.02)' }}>
                                            <Box sx={{ p: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                                                    <Avatar sx={{ bgcolor: 'rgba(255, 140, 0, 0.1)', color: '#ff8c00' }}>
                                                        <SchoolIcon />
                                                    </Avatar>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                                        Énoncé de l'exercice
                                                    </Typography>
                                                </Box>
                                                <Box className="exercise-statement">
                                                    {(currentLesson.exercise.statementBlocks || []).map((block: any) => (
                                                        <BlockRenderer key={block.id} block={block} />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Card>

                                        {/* Learner Input Area */}
                                        <Card sx={{ borderRadius: 6, mb: 4, p: 4, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 10px 30px rgba(0,0,0,0.02)' }}>
                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                                Votre réponse
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={6}
                                                placeholder="Saisissez votre réponse ici..."
                                                value={learnerAnswer}
                                                onChange={(e) => setLearnerAnswer(e.target.value)}
                                                variant="outlined"
                                                sx={{
                                                    mb: 3,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 4,
                                                        fontFamily: 'Inter, sans-serif',
                                                    }
                                                }}
                                            />
                                            
                                            {currentLesson.exercise.aiCorrectionEnabled && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={isEvaluating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                                            disabled={isEvaluating || !learnerAnswer.trim() || aiCorrectionCount >= 2}
                                                            onClick={handleEvaluateExercise}
                                                            sx={{
                                                                borderRadius: 4,
                                                                textTransform: 'none',
                                                                fontWeight: 800,
                                                                px: 4,
                                                                py: 1.5,
                                                                background: aiCorrectionCount >= 2 ? 'rgba(0,0,0,0.1)' : 'linear-gradient(45deg, #667eea, #764ba2)',
                                                                boxShadow: aiCorrectionCount >= 2 ? 'none' : '0 10px 20px rgba(102, 126, 234, 0.2)',
                                                                '&:hover': {
                                                                    background: aiCorrectionCount >= 2 ? 'rgba(0,0,0,0.1)' : 'linear-gradient(45deg, #5a67d8, #6b46c1)',
                                                                }
                                                            }}
                                                        >
                                                            {isEvaluating ? 'Analyse en cours...' : aiCorrectionCount >= 2 ? 'Limite de feedback atteinte' : `Demander un feedback de l'IA (${aiCorrectionCount}/2)`}
                                                        </Button>
                                                    </Box>

                                                    {aiFeedback && (
                                                        <Fade in={!!aiFeedback}>
                                                            <Box sx={{ 
                                                                p: 3, 
                                                                borderRadius: 4, 
                                                                bgcolor: aiFeedback.isCorrect ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255, 140, 0, 0.05)',
                                                                border: `1px solid ${aiFeedback.isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 140, 0, 0.1)'}`,
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: aiFeedback.isCorrect ? '#10b981' : '#ff8c00', color: '#fff' }}>
                                                                            <AutoAwesomeIcon fontSize="small" />
                                                                        </Avatar>
                                                                        <Typography sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                                                            Feedback de l'IA
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ bgcolor: '#fff', px: 2, py: 0.5, borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)' }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: aiFeedback.score >= 7 ? '#10b981' : aiFeedback.score >= 5 ? '#ff8c00' : '#ef4444' }}>
                                                                            Score : {aiFeedback.score}/10
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Typography 
                                                                    variant="body1" 
                                                                    sx={{ color: '#4a5568', lineHeight: 1.6 }}
                                                                    dangerouslySetInnerHTML={{ __html: aiFeedback.feedback }}
                                                                />
                                                            </Box>
                                                        </Fade>
                                                    )}
                                                </Box>
                                            )}
                                        </Card>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={showCorrection ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                onClick={() => setShowCorrection(!showCorrection)}
                                                sx={{
                                                    borderRadius: 4,
                                                    textTransform: 'none',
                                                    fontWeight: 800,
                                                    px: 4,
                                                    py: 1.5,
                                                    background: showCorrection ? 'rgba(0,0,0,0.05)' : 'linear-gradient(45deg, #ff8c00, #ffae42)',
                                                    color: showCorrection ? '#666' : '#fff',
                                                    boxShadow: showCorrection ? 'none' : '0 10px 20px rgba(255, 140, 0, 0.2)',
                                                    '&:hover': {
                                                        background: showCorrection ? 'rgba(0,0,0,0.1)' : 'linear-gradient(45deg, #e67e00, #ffa500)',
                                                    }
                                                }}
                                            >
                                                {showCorrection ? 'Masquer la correction' : 'Voir la correction suggérée'}
                                            </Button>
                                        </Box>

                                        {showCorrection && (
                                            <Fade in={showCorrection}>
                                                <Card sx={{ 
                                                    borderRadius: 6, 
                                                    p: 4, 
                                                    bgcolor: 'rgba(255, 140, 0, 0.02)', 
                                                    border: '1px dashed rgba(255, 140, 0, 0.3)',
                                                    boxShadow: '0 10px 30px rgba(255, 140, 0, 0.05)',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    mt: 4
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                                        <Avatar sx={{ bgcolor: '#ff8c00', color: '#fff' }}>
                                                            <CheckCircleIcon />
                                                        </Avatar>
                                                        <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', color: '#ff8c00' }}>
                                                            Proposition de correction
                                                        </Typography>
                                                    </Box>
                                                    <Box className="exercise-correction">
                                                        {(currentLesson.exercise?.correctionBlocks || []).map((block: any) => (
                                                            <BlockRenderer key={block.id} block={block} />
                                                        ))}
                                                    </Box>
                                                </Card>
                                            </Fade>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Footer Navigation - Hidden in preview mode */}
                            {!isPreview && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 8 }}>
                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={handleNext}
                                        size="large"
                                        disabled={currentLesson?.type === 'quiz' && (quizScore === null || quizScore < 50)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderRadius: 50,
                                            px: 6,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #5a67d8 0%, #6b46c1 100%)',
                                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                                            }
                                        }}
                                    >
                                        {isMobile ? 'Terminer' : 'Marquer comme terminé et continuer'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', mt: 10 }}>
                            <img src="/assets/illustrations/select-lesson.svg" alt="Select lesson" style={{ maxWidth: 300, opacity: 0.5, marginBottom: 20 }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                            <Typography variant="h5" color="text.secondary" fontFamily="Quicksand, sans-serif">Sélectionnez une leçon pour commencer.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Q&A Panel */}
            <Drawer
                anchor="right"
                open={showQA}
                onClose={() => setShowQA(false)}
                sx={{
                    [`& .MuiDrawer-paper`]: { width: 400, boxSizing: 'border-box' },
                }}
            >
                {currentLesson && (
                    <LessonQA
                        courseId={courseId!}
                        lessonId={currentLesson._id}
                        courseName={decodeHTMLEntities(course.title)}
                        lessonName={decodeHTMLEntities(currentLesson.title)}
                    />
                )}
            </Drawer>

            {/* Report Problem Modal */}
            <ReportProblemModal
                open={showReportModal}
                onClose={() => setShowReportModal(false)}
                defaultSubject={currentLesson ? `Un problème avec le contenu (${course.title} - ${currentLesson.title})` : undefined}
            />
        </Box>
    );
};

export default CoursePlayer;
